import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { useColorScheme } from '#pplx/frontend/aether/components/ColorSchemeProvider/useColorScheme.js';
import { getOverlayZIndex } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { MobileBottomSheet } from '#pplx/frontend/aether/components/internal/menu/MobileBottomSheet.js';
import { PopoverContext } from '#pplx/frontend/aether/components/internal/surface-content/PopoverContext.js';
import { useElevation, useNearestSurface, } from '#pplx/frontend/aether/components/Surface/useSurface.js';
import { useOverlay } from '#pplx/frontend/aether/internal/overlay/useOverlay.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { PopoverPrimitive } from '#pplx/frontend/aether/primitives/PopoverPrimitive/index.js';
import { PopoverPrimitiveHoverGroup } from '#pplx/frontend/aether/primitives/PopoverPrimitive/PopoverPrimitiveHoverGroup.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
/**
 * Groups hover popovers so the first card respects its open delay, subsequent cards open
 * instantly, and sibling replacements skip both open and close animations. Nested groups are
 * independent.
 */
export const PopoverHoverGroup = PopoverPrimitiveHoverGroup;
const SCROLL_AREA_CLASSNAME = 'rounded-xl';
const SCROLL_AREA_CONTENT_CLASSNAME = 'p-2';
const popoverContentVariants = cva([
    // Shape
    'rounded-xl shadow-md forced-colors:border forced-colors:border-[CanvasText]',
    // Transform origin - automatically calculated by Radix based on trigger position
    'origin-[var(--radix-popover-content-transform-origin)]',
], {
    variants: {
        appearance: {
            dark: 'bg-inverse dark:bg-raised',
            light: 'bg-raised',
        },
        disableAnimation: {
            false: [
                // Opening animation
                'data-[state=open]:animate-scaleAndFadeIn',
                // Closing animation
                'data-[state=closed]:animate-scaleAndFadeOut',
            ],
            true: '',
        },
    },
    defaultVariants: {
        appearance: 'dark',
        disableAnimation: false,
    },
});
/**
 * Floating content panel supporting click and hover interactions with smart
 * positioning and portal rendering. On mobile renders as a bottom sheet.
 *
 * @example
 * ```tsx
 * // Click interaction (default, uses system theme)
 * <Popover triggerElement={<Button icon={IconLock}>Locked Feature</Button>}>
 *   <div>
 *     <h3>Premium Feature</h3>
 *     <p>Upgrade to access this feature</p>
 *     <Button>Upgrade Now</Button>
 *   </div>
 * </Popover>
 *
 * // Force specific theme
 * <Popover
 *   appearance="light"
 *   triggerElement={<Button>Info</Button>}
 * >
 *   <div>This popover will be light regardless of system theme</div>
 * </Popover>
 *
 * // Force specific theme
 * <Popover
 *   appearance="dark"
 *   triggerElement={<Button>Info</Button>}
 * >
 *   <div>This popover will be dark regardless of system theme</div>
 * </Popover>
 *
 * // Hover interaction. The group is optional; use it for instant sibling replacements.
 * <PopoverHoverGroup>
 *   <Popover
 *     interaction="hover"
 *     openDelayMs={200}
 *     closeDelayMs={300}
 *     triggerElement={<Button>First preview</Button>}
 *   >
 *     <div>First preview content</div>
 *   </Popover>
 *   <Popover
 *     interaction="hover"
 *     openDelayMs={200}
 *     closeDelayMs={300}
 *     triggerElement={<Button>Second preview</Button>}
 *   >
 *     <div>Second preview content</div>
 *   </Popover>
 * </PopoverHoverGroup>
 * ```
 *
 * @category components
 * @keywords popover, components, floating, content, panel, click, hover, interactions, smart,
 *   positioning, anchor, anchored, anchorRef, SurfaceContentHandle, usePopover, content bounds,
 *   separate anchor, separate trigger, matched width,
 *   width matching, matchTriggerWidth, trigger width, anchor width, fixed width, content width,
 *   width opt out, portal, rendering, mobile, renders, bottom, sheet, alignoffset, alignment,
 *   focus, autofocus, onfocusoutside, shouldautofocus, disabled, disableAnimation, animation,
 *   instant, command-menu, command menu, combobox, autocomplete, typeahead, surface, clipping,
 *   clipped, collision, boundary, overflow, title, showTitleOnDesktop, heading, bottom sheet
 *   title, tooltip, toggle-tip, menu
 * @whenToUse Use Popover to display rich, interactive content anchored to a trigger element.
 *   Unlike Tooltip (which shows plain text on hover), Popover supports buttons, links, forms, and
 *   structured layouts within its content area. It is ideal for feature previews, contextual
 *   actions, inline forms, and any UI pattern where a floating panel needs to contain interactive
 *   elements. Use anchorRef for click popovers when a compact trigger should open a panel
 *   positioned against a larger existing surface, such as an input container. On mobile, it
 *   automatically adapts to a bottom sheet.
 * @guidelineDo
 *   - Use the click interaction (default) for popovers containing interactive content like
 *     buttons, links, or forms
 *   - Use the hover interaction for preview-style popovers that show supplementary,
 *     non-interactive information
 *   - PopoverHoverGroup is optional. Wrap related hover popovers only when moving between them
 *     should skip the opening delay and animations while immediately replacing the previously
 *     open card
 *   - Set closeOnScroll to false for stationary hover navigation whose actions scroll other
 *     content; the consumer must still dismiss previews when its own trigger rail scrolls
 *   - Set the appearance prop to 'dark', 'light', or 'system' to match the surrounding UI context
 *   - Use the controlled mode (open + onOpenChange) when popover visibility needs to be managed by
 *     parent state
 *   - Pass onOpenChange without open when you only need to react to open and close while the
 *     popover manages its own state
 *   - Use alignOffset to shift the panel along the alignment axis without changing the trigger
 *     distance
 *   - Use anchorRef on click popovers when the panel should align to an existing container or
 *     input surface instead of the trigger; the content width follows the anchor by default
 *   - Use usePopover inside content to measure or observe its nearest floating
 *     surface without exposing the DOM node; bounds are null for mobile bottom sheets
 *   - Set matchTriggerWidth to false when the panel should size to its own content instead of
 *     matching a wide trigger or anchor, while still respecting minWidthPx and maxWidthPx
 *   - Use the title prop for the popover heading instead of duplicating a heading inside
 *     children — mobile bottom sheets display it automatically, and showTitleOnDesktop opts the
 *     desktop popover into rendering it
 *   - Set autoFocusFirstFocusableElement to true on click popovers containing buttons or links
 *     when shouldAutoFocus remains true
 *   - Set shouldAutoFocus to false on click popovers only when you need to preserve the current
 *     focus target across open and close
 *   - Use disableAnimation for command-menu, combobox, autocomplete, or typeahead surfaces where
 *     the panel should appear immediately while filtering
 *   - Wrap overflow-constrained containers such as side panels in Surface so the popover portals
 *     into and keeps its positioning within the nearest Surface boundary instead of being clipped;
 *     this also keeps popovers inside Modal content positioned within the modal panel
 * @guidelineDont
 *   - Don't use Popover for simple text labels — use Tooltip instead, which is optimized for
 *     plain-text hover labels
 *   - Don't set maxWidthPx too small for content-heavy popovers — the default 320px works well for
 *     most use cases
 *   - Don't use hover interaction for content that requires user interaction — hover popovers
 *     close when the cursor leaves
 *   - Don't pass open without onOpenChange — controlled mode needs both; onOpenChange on its own
 *     is valid and notifies you of changes while the popover keeps owning its open state
 *   - Don't rely on onFocusOutside, shouldAutoFocus, or autoFocusFirstFocusableElement for hover
 *     popovers — hover uses HoverCard
 *   - Don't rely on anchorRef for hover popovers — hover popovers position from their trigger
 *   - Don't nest Popovers — use a single Popover with structured content instead
 * @accessibility
 *   - Click popovers use Radix Popover for Escape key dismissal, trigger aria state, outside-focus
 *     events, and open/close auto-focus by default
 *   - Hover popovers use Radix HoverCard and do not receive the click-only focus props
 *     onFocusOutside, shouldAutoFocus, or autoFocusFirstFocusableElement
 *   - Set shouldAutoFocus to false on click popovers to prevent Radix's open and close auto-focus
 *     behavior
 *   - On mobile viewports, both click and hover popovers automatically render as a
 *     MobileBottomSheet dialog for better touch interaction
 *   - When title is provided, the mobile bottom sheet displays it as its visible heading and
 *     uses it as the dialog accessible name; desktop popovers render the title only when
 *     showTitleOnDesktop is true
 *   - Click popover triggers receive aria-haspopup and aria-expanded attributes through Radix
 *     Popover; do not assume those click-trigger attributes for hover popovers
 *   - When using anchorRef, triggerElement remains the interactive control, so keep its visible
 *     label and accessible name clear
 *   - When disabled is true, only the trigger element renders — no popover markup is added to the
 *     DOM, keeping the accessibility tree clean
 * @relatedComponents
 *   - tooltip
 *   - toggle-tip
 *   - menu
 *   - surface
 */
export function Popover({ triggerElement, children, appearance = 'system', side = 'bottom', align = 'center', alignOffset = 0, open, onOpenChange, minWidthPx, maxWidthPx = 320, offsetPx = 8, shouldShowArrow = false, disabled = false, interaction = 'click', openDelayMs = 200, closeDelayMs = 300, closeOnScroll = true, onWheelContent: _onWheelContent, onContentMouseEnter, onContentMouseLeave, onFocusOutside, shouldAutoFocus = true, disableAnimation = false, autoFocusFirstFocusableElement = false, anchorRef, matchTriggerWidth = true, contentPadding = 'sm', title, showTitleOnDesktop = false, ...props }) {
    const contentRef = useRef(null);
    const contentHandle = useMemo(() => ({
        getContentBounds: () => {
            const rect = contentRef.current?.getBoundingClientRect();
            return rect ? { bottomPx: rect.bottom, leftPx: rect.left, widthPx: rect.width } : null;
        },
        registerOnContentResize: (onResize) => {
            const element = contentRef.current;
            if (!element || typeof ResizeObserver === 'undefined')
                return () => { };
            const observer = new ResizeObserver(onResize);
            observer.observe(element);
            return () => observer.disconnect();
        },
    }), []);
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const { isMobileStyle } = useAetherContext();
    const portalTargetElement = useNearestSurface();
    const resolvedPortalTarget = shouldUseActivationOrder ? undefined : portalTargetElement;
    const parentElevation = useElevation();
    const { isOpen, handleOpenChange } = useOverlay({ open, onOpenChange });
    const [collisionBoundary, setCollisionBoundary] = useState();
    const colorScheme = useColorScheme();
    const hasAnchorRef = anchorRef != null;
    useLayoutEffect(() => {
        if (!isOpen || disabled || isMobileStyle)
            return;
        // Legacy zero-sized portal targets cannot constrain an overlay. Measure
        // only while open, retaining the boundary through the exit animation.
        const boundary = resolvedPortalTarget != null &&
            resolvedPortalTarget.offsetWidth > 0 &&
            resolvedPortalTarget.offsetHeight > 0
            ? resolvedPortalTarget
            : undefined;
        if (boundary !== collisionBoundary)
            setCollisionBoundary(boundary);
    }, [isOpen, disabled, isMobileStyle, resolvedPortalTarget, collisionBoundary]);
    // Apply overlay-tier z-index so the popover renders above sibling Surfaces
    // that may paint later in the DOM (e.g. a side panel next to the Surface
    // the popover portals into). Follows the same pattern as Modal.
    const overlayZIndexStyle = useMemo(() => shouldUseActivationOrder ? undefined : { zIndex: getOverlayZIndex(parentElevation) }, [parentElevation, shouldUseActivationOrder]);
    // If not using system color scheme, use the appearance prop directly (light or dark)
    // Else, the color scheme of the system will be used
    // Default to dark theme if the system color scheme is not set
    const computedAppearance = (() => {
        if (appearance !== 'system') {
            return appearance;
        }
        return colorScheme?.colorScheme || 'dark';
    })();
    // Manually pick the props that Radix UI passes in for their own internal usage
    const __radixProps = useRadixProps(props);
    // Radix rotates the arrow for each placement, keeping these two bordered
    // edges facing away from the popover.
    const arrowClasses = classNames('size-2 -translate-y-1 rotate-45 border-b border-r border-transparent ring-1 ring-soft [clip-path:polygon(0_100%,100%_0,150%_0,150%_150%,0_150%)] dark:border-soft dark:ring-0 dark:[clip-path:none]', {
        'bg-inverse dark:bg-raised': computedAppearance === 'dark',
        'bg-raised': computedAppearance === 'light',
    });
    const hoverContentClasses = interaction === 'hover'
        ? [
            'origin-[var(--radix-hover-card-content-transform-origin)]',
            // Keep instant opening separate from sibling replacement so ordinary closes still animate.
            'data-[hover-group-instant-open]:data-[state=open]:!animate-none',
            'data-[hover-group-sibling-replacement]:data-[state=closed]:!animate-none',
        ]
        : undefined;
    if (disabled) {
        return (_jsx(PopoverContext.Provider, { value: contentHandle, children: triggerElement }));
    }
    // Render as mobile bottom sheet on mobile devices
    // Note: Hover popovers are converted to click interaction on mobile
    if (isMobileStyle) {
        return (_jsx(PopoverContext.Provider, { value: contentHandle, children: _jsx(MobileBottomSheet, { isOpen: isOpen, onToggle: handleOpenChange, triggerElement: triggerElement, title: title, children: _jsx("div", { className: "text-sm", children: children }) }) }));
    }
    // 16px = 8px vertical padding (top + bottom from p-1) + 8px margin
    const maxHeight = contentPadding === 'none'
        ? 'var(--radix-popper-available-height)'
        : 'calc(var(--radix-popper-available-height) - var(--spacing-4))';
    const scrollAreaContentClassName = contentPadding === 'none' ? undefined : SCROLL_AREA_CONTENT_CLASSNAME;
    // Preserve the existing Aether content sizing while the primitive also owns
    // the Radix content sizing strategy for shared consumers.
    const matchedAnchorContentWidth = hasAnchorRef && matchTriggerWidth
        ? 'calc(var(--radix-popover-trigger-width) - var(--spacing-4))'
        : undefined;
    const content = (_jsxs("div", { className: "text-sm text-primary", "data-color-scheme": computedAppearance === 'dark' ? 'dark' : undefined, style: {
            minWidth: matchedAnchorContentWidth ?? minWidthPx,
            maxWidth: matchedAnchorContentWidth ?? maxWidthPx,
        }, children: [showTitleOnDesktop && title && _jsx("div", { className: "mb-2 font-medium", children: title }), children] }));
    const popoverArrowProps = shouldShowArrow === true
        ? {
            shouldShowArrow,
            arrowSlotProps: {
                className: arrowClasses,
                children: _jsx("div", {}),
            },
        }
        : { shouldShowArrow };
    return (_jsx(PopoverContext.Provider, { value: contentHandle, children: _jsx(PopoverPrimitive, { contentRef: contentRef, triggerElement: triggerElement, interaction: interaction, open: isOpen, onOpenChange: handleOpenChange, side: side, align: align, alignOffset: alignOffset, offsetPx: offsetPx, openDelayMs: openDelayMs, closeDelayMs: closeDelayMs, closeOnScroll: closeOnScroll, anchorRef: anchorRef, matchTriggerWidth: matchTriggerWidth, portalContainer: resolvedPortalTarget, collisionBoundary: collisionBoundary, collisionPadding: 10, onWheelContent: _onWheelContent, onContentMouseEnter: onContentMouseEnter, onContentMouseLeave: onContentMouseLeave, onFocusOutside: onFocusOutside, shouldAutoFocus: shouldAutoFocus, autoFocusFirstFocusableElement: autoFocusFirstFocusableElement, ...popoverArrowProps, __radixTriggerProps: __radixProps, contentSlotProps: {
                className: classNames(popoverContentVariants({
                    appearance: computedAppearance,
                    disableAnimation,
                }), hoverContentClasses),
                style: overlayZIndexStyle,
            }, scrollAreaSlotProps: {
                className: SCROLL_AREA_CLASSNAME,
                maxHeight,
            }, scrollAreaContentSlotProps: {
                className: scrollAreaContentClassName,
            }, children: content }) }));
}
//# sourceMappingURL=index.js.map